if (!customElements.get('product-upsell')) {
  customElements.define(
    'product-upsell',
    class ProductUpsell extends HTMLElement {
      #cartUpdateUnsubscriber;

      connectedCallback() {
        this.addEventListener('click', this.#onClick);
        this.#cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.#syncPageProductInCart);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.#cartUpdateUnsubscriber?.();
      }

      /**
       * Show compare-at price and save badge when the product-page product is in the cart.
       * @param {{ cartData?: { items?: Array<{ product_id: number }>, item_count?: number, product_id?: number } }} [event]
       */
      #syncPageProductInCart = async (event) => {
        const productId = Number(this.dataset.pageProductId);
        if (!productId) return;

        const inCart = await this.#isPageProductInCart(productId, event?.cartData);
        this.classList.toggle('product-upsell--page-in-cart', inCart);
      };

      /**
       * @param {number} productId
       * @param {{ items?: Array<{ product_id: number }>, item_count?: number, product_id?: number }} [cartData]
       * @returns {Promise<boolean>}
       */
      async #isPageProductInCart(productId, cartData) {
        if (Array.isArray(cartData?.items)) {
          return cartData.items.some((item) => Number(item.product_id) === productId);
        }

        if (Number(cartData?.item_count) === 0) return false;

        if (Number(cartData?.product_id) === productId) return true;

        try {
          const response = await fetch(`${window.routes.cart_url}.js`);
          const cart = await response.json();
          return Array.isArray(cart.items) && cart.items.some((item) => Number(item.product_id) === productId);
        } catch (error) {
          console.error(error);
          return this.classList.contains('product-upsell--page-in-cart');
        }
      }

      /**
       * @param {MouseEvent} event
       */
      #onClick = (event) => {
        const button = event.target.closest('[data-upsell-add]');
        if (!button || !this.contains(button) || button.disabled) return;
        event.preventDefault();
        this.#addToCart(button);
      };

      /**
       * @param {HTMLButtonElement} button
       */
      async #addToCart(button) {
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }
        config.body = formData;

        try {
          const response = await fetch(`${window.routes.cart_add_url}`, config);
          const data = await response.json();

          if (data.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-upsell',
              productVariantId: variantId,
              errors: data.errors || data.description,
              message: data.message,
            });
            return;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-upsell',
            productVariantId: variantId,
            cartData: data,
          });

          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data);
        } catch (error) {
          console.error(error);
        } finally {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      }
    }
  );
}
